import LayersIcon from "@redotech/redo-icons/layers-three-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { MultiToteVerificationData } from "../scanning-context/types";

type MultiToteVerificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  data?: MultiToteVerificationData;
};

export const MultiToteVerificationModal = memo(
  function MultiToteVerificationModal({
    isOpen,
    onClose,
    onContinue,
    data,
  }: MultiToteVerificationModalProps) {
    const [checkedTotes, setCheckedTotes] = useState<Set<string>>(
      new Set(data?.scannedToteId ? [data.scannedToteId] : []),
    );

    // Reset checked totes when modal opens with new data
    const [prevScannedToteId, setPrevScannedToteId] = useState<
      string | undefined
    >(data?.scannedToteId);
    if (data?.scannedToteId !== prevScannedToteId) {
      setPrevScannedToteId(data?.scannedToteId);
      setCheckedTotes(new Set(data?.scannedToteId ? [data.scannedToteId] : []));
    }

    const allTotesChecked =
      data?.toteIds && checkedTotes.size === data.toteIds.length;

    const handleCheckboxChange = (toteId: string, checked: boolean) => {
      setCheckedTotes((prev) => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(toteId);
        } else {
          newSet.delete(toteId);
        }
        return newSet;
      });
    };

    if (!data) {
      return null;
    }

    return (
      <RedoModal
        isOpen={isOpen}
        modalSize="sm"
        onModalCloseRequested={onClose}
        theme="brand"
        title={`Multi-tote fulfillment (${data.toteIds.length} totes)`}
        TitleIcon={LayersIcon}
      >
        <Flex dir="column" gap="xl" w="full">
          <Text fontSize="sm" textColor="secondary">
            Confirm that you have all of the totes for this fulfillment before
            packing.
          </Text>

          <Flex dir="column" gap="md" w="full">
            {data.toteIds.map((toteId) => {
              const toteName = data.toteNames[toteId] || toteId;
              const isScannedTote = toteId === data.scannedToteId;
              const isChecked = checkedTotes.has(toteId);

              return (
                <Flex align="center" gap="md" key={toteId}>
                  <RedoCheckbox
                    disabled={isScannedTote}
                    label={toteName}
                    setValue={(value) =>
                      handleCheckboxChange(toteId, value === true)
                    }
                    size="md"
                    value={isChecked}
                  />
                  {isScannedTote && (
                    <Text fontSize="xs" textColor="tertiary">
                      (scanned)
                    </Text>
                  )}
                </Flex>
              );
            })}
          </Flex>

          <Flex gap="md" justify="flex-end" w="full">
            <RedoButton
              hierarchy="secondary"
              onClick={onClose}
              size="md"
              text="Cancel"
            />
            <RedoButton
              disabled={!allTotesChecked}
              hierarchy="primary"
              onClick={onContinue}
              size="md"
              text="Confirm"
              theme="success"
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
